/**
 * AddBuildingModal - "Add Building" popup for creating a location without
 * leaving the page you're on.
 *
 * Two callers share this one form:
 *	 1. The Buildings page ("Add Building" in the page header) — the full
 *	    form, including the picker for which customers the building
 *	    belongs to.
 *	 2. The work order / inspection create form — opened from the "Add a New
 *	    Building" link under the Building dropdown when the customer's
 *	    building isn't in the system yet. That flow passes `lockedCompanyId`,
 *	    so the customer assignment is fixed to the company already chosen on
 *	    the job form and the customer picker collapses to a plain confirmation
 *	    line. On success the job form selects the new building and fills in
 *	    the project address from it.
 *
 * The fields themselves are the shared <BuildingFieldset> — the same molecule
 * Admin → Locations renders — so the two ways to create a building stay one
 * form. This organism only adds the modal chrome, the option fetches, and the
 * POST /locations call, which invalidates the locations cache so every listing
 * and dropdown picks the new building up.
 *
 * @example
 * <AddBuildingModal isOpen={open} onClose={() => setOpen(false)} lockedCompanyId={companyId} onCreated={(b) => selectBuilding(b)} />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../Modal/Modal';
import { Alert, Button } from '../../atoms';
import { BuildingFieldset } from '../../molecules/BuildingFieldset';
import type { BuildingFormValues } from '../../molecules/BuildingFieldset';
import { api } from '../../../services/api';
import { businessLineService, BusinessLine } from '../../../services/businessLineService';
import { qk } from '../../../lib/queryKeys';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** The created location as the API hands it back (POST /locations → { data }).
 *	Address fields are nullable — only the name is required at creation. */
export interface CreatedBuilding {
	id: string | number;
	name: string;
	address?: string | null;
	city?: string | null;
	state?: string | null;
	zip?: string | null;
	business_line?: string | null;
}

/** Company option for the customer assignment list */
interface CompanyOption {
	id: string | number;
	name: string;
}

/** Editable fields on the Add Building form. Owned by the shared fieldset —
 *	the same shape Admin -> Locations edits, so the two forms can't drift. */
type BuildingFormData = BuildingFormValues;

export interface AddBuildingModalProps {
	/** Whether the modal is visible. */
	isOpen: boolean;
	/** Called when the modal should close (X, Escape, backdrop, Cancel). */
	onClose: () => void;
	/** Fired after a successful create with the new building record. */
	onCreated?: (building: CreatedBuilding) => void;
	/**
	 * Pre-assigns the new building to this customer and hides the customer
	 * picker. Used by the job form flow, where the customer was already
	 * chosen on the form and the building must land under it — letting the
	 * user pick a different customer there would create a building that
	 * can't appear in the dropdown they opened this modal from.
	 */
	lockedCompanyId?: string | number;
}

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Id linking the Add Building <form> to the Create button pinned in the modal
 *  footer (the button sits outside the form element, so it needs form="…"). */
const BUILDING_FORM_ID = 'add-building-form';

/** Default empty form state */
const EMPTY_FORM: BuildingFormData = {
	name: '',
	address: '',
	address_line_2: '',
	city: '',
	state: '',
	zip: '',
	cleanable_sqft: '',
	tax_rate: '',
	business_line: '',
	notes: '',
	company_ids: [],
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const AddBuildingModal: React.FC<AddBuildingModalProps> = ({
	isOpen,
	onClose,
	onCreated,
	lockedCompanyId,
}) => {
	const queryClient = useQueryClient();

	/* --- Form state --- */
	const [form, setForm] = useState<BuildingFormData>(EMPTY_FORM);
	const [formError, setFormError] = useState<string | null>(null);
	const [formSaving, setFormSaving] = useState(false);

	/* ============================================
	 * DATA FETCHING
	 * Both lists are only fetched while the modal is open — this component
	 * mounts alongside the job form, and an unopened modal shouldn't add
	 * two requests to every work order / inspection create page load.
	 * ============================================ */

	/** Customers available for assignment (also supplies the locked-customer name) */
	const companiesQuery = useQuery({
		queryKey: qk.companies.lists(),
		queryFn: async () => {
			const res = await api.get<any>('/companies');
			const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
			return data.map((c: any) => ({ id: c.company_id || c.id, name: c.name })) as CompanyOption[];
		},
		enabled: isOpen,
	});
	const companies: CompanyOption[] = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);

	/* CMS-managed Business Lines. Active-only so the picker matches what's
	 * selectable on the WO / Inspection forms. */
	const businessLinesQuery = useQuery({
		queryKey: qk.businessLines.list({ active: true }),
		queryFn: async () => {
			const res = await businessLineService.list(true);
			return ((res.data as any)?.data ?? []) as BusinessLine[];
		},
		enabled: isOpen,
	});
	const businessLines: BusinessLine[] = useMemo(() => businessLinesQuery.data ?? [], [businessLinesQuery.data]);

	/** Display name for the locked customer, shown in place of the picker */
	const lockedCompanyName = useMemo(() => {
		if (!lockedCompanyId) return null;
		return companies.find((c) => String(c.id) === String(lockedCompanyId))?.name ?? null;
	}, [companies, lockedCompanyId]);

	/* ============================================
	 * FORM RESET
	 * ============================================ */

	/* Start every open from a clean form, seeded with the locked customer
	   when the job form supplied one. */
	useEffect(() => {
		if (!isOpen) return;
		setForm({ ...EMPTY_FORM, company_ids: lockedCompanyId ? [String(lockedCompanyId)] : [] });
		setFormError(null);
	}, [isOpen, lockedCompanyId]);

	/* ============================================
	 * SUBMIT
	 * ============================================ */

	/** Create-building mutation. On success it invalidates the locations
	 * cache so the listing (and any other location consumer) refetches. */
	const createMutation = useMutation({
		mutationFn: (payload: Record<string, any>) => api.post<any>('/locations', payload),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.locations.all }),
	});

	/** Merges a patch from the fieldset into the form state. */
	const handleFieldChange = useCallback((patch: Partial<BuildingFormData>) => {
		setForm((prev) => ({ ...prev, ...patch }));
	}, []);

	/**
	 * Submits the Add Building form via POST /locations.
	 * Optional fields are omitted rather than sent empty so the location row
	 * keeps NULLs instead of empty strings.
	 */
	const handleSubmit = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);

		/* Validate required fields */
		if (!form.name.trim()) {
			setFormError('Building name is required.');
			return;
		}

		/* At least one customer is required. A building with no customer link
		   has no pivot row, so it never appears in the Building dropdown on the
		   work order / inspection create forms — it silently exists but can't be
		   used. Catching it here beats letting someone create an invisible
		   record and wonder why their building never shows up. */
		if (form.company_ids.length === 0) {
			setFormError('Select at least one customer. Buildings not linked to a customer will not appear when creating work orders or inspections.');
			return;
		}

		setFormSaving(true);

		try {
			const payload: Record<string, any> = {
				name: form.name.trim(),
			};
			if (form.address.trim()) payload.address = form.address.trim();
			if (form.address_line_2.trim()) payload.address_line_2 = form.address_line_2.trim();
			if (form.city.trim()) payload.city = form.city.trim();
			if (form.state.trim()) payload.state = form.state.trim();
			if (form.zip.trim()) payload.zip = form.zip.trim();
			if (form.cleanable_sqft) payload.cleanable_sqft = parseInt(form.cleanable_sqft, 10);
			if (form.tax_rate) payload.tax_rate = parseFloat(form.tax_rate) / 100;
			if (form.business_line) payload.business_line = form.business_line;
			if (form.notes.trim()) payload.notes = form.notes.trim();
			if (form.company_ids.length > 0) payload.company_ids = form.company_ids;

			const res = await createMutation.mutateAsync(payload);
			/* POST /locations responds { data: location } — fall back to the
			   body itself so a bare-record response still reaches the caller. */
			const created: CreatedBuilding = (res.data?.data ?? res.data) as CreatedBuilding;

			onClose();
			onCreated?.(created);
		} catch (err: any) {
			const msg = err?.data?.message || 'Failed to create building. Please check your input.';
			setFormError(msg);
		} finally {
			setFormSaving(false);
		}
	}, [form, onClose, onCreated, createMutation]);

	/* ============================================
	 * RENDER
	 * ============================================ */

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Add Building"
			size="md"
			dataRef="organism-add-building-modal"
			/* Actions are pinned in the modal footer instead of trailing the
			   form — this form is far taller than a phone/laptop viewport and
			   the buttons were being clipped at the bottom of the panel. */
			footer={(
				<div className="add-building-modal__actions flex flex-col-reverse tablet:flex-row tablet:justify-end gap-8px">
					<Button type="button" variant="secondary" size="sm" onClick={onClose} className="w-full tablet:w-auto">
						Cancel
					</Button>
					<Button type="submit" form={BUILDING_FORM_ID} variant="primary" size="sm" disabled={formSaving} className="w-full tablet:w-auto">
						{formSaving ? 'Creating...' : 'Create Building'}
					</Button>
				</div>
			)}
		>
			{/* Error banner inside modal */}
			{formError && (
				<Alert variant="error" className="mb-12px">{formError}</Alert>
			)}

			{/* Every field lives in the shared fieldset; this organism owns only
			    the <form> element the footer's submit button is wired to. */}
			<form id={BUILDING_FORM_ID} onSubmit={handleSubmit}>
				<BuildingFieldset
					idPrefix="bldg"
					values={form}
					onChange={handleFieldChange}
					businessLines={businessLines}
					companies={companies}
					companyLocked={Boolean(lockedCompanyId)}
					lockedCompanyName={lockedCompanyName}
					dataRef="organism-add-building-modal-fields"
				/>
			</form>
		</Modal>
	);
};

export default AddBuildingModal;
